import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useChainId } from 'wagmi';
import { ArrowUpDown, Search, Copy, Check, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { orpc } from '@/shared/api/client';
import { galactica, galacticaCassiopeia } from '@/shared/config';

interface User {
  entryId: string;
  points: number;
  rank: number;
  vegnet: string | null;
  walletAddresses: string[] | null;
  primaryWallet: string | null;  // Wallet with highest veGNET
  updatedAt: number;
}

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Extract just the user ID part (before ::)
  const displayId = id.includes('::') ? id.split('::')[0] : id;

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm">{displayId}</span>
      <button onClick={copy} className="text-[#807f7e] hover:text-[#2b2b2b]">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}


const PAGE_SIZE = 20;

export function UsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const chainId = useChainId();
  const currentChain = chainId === galactica.id ? galactica : galacticaCassiopeia;
  const explorerUrl = currentChain.blockExplorers?.default.url || 'https://explorer.galactica.com';

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchUsers = async (pageNum = page) => {
    setLoading(true);
    setError(null);
    try {
      const result = await orpc.users.list({ page: pageNum, pageSize: PAGE_SIZE });
      setUsers(result.users);
      setTotal(result.total);
    } catch (err) {
      setError(String(err));
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'rank',
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          Rank
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="font-medium">#{row.getValue('rank')}</span>
      ),
    },
    {
      accessorKey: 'entryId',
      header: 'Entry ID',
      cell: ({ row }) => <CopyableId id={row.getValue('entryId')} />,
    },
    {
      accessorKey: 'points',
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-3"
        >
          UBI Points
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span>{(row.getValue('points') as number).toLocaleString()}</span>
      ),
    },
    {
      accessorKey: 'vegnet',
      header: 'veGNET',
      cell: ({ row }) => {
        const vegnet = row.getValue('vegnet') as string | null;
        if (!vegnet) return <span className="text-[#807f7e]">—</span>;
        try {
          const parsed = JSON.parse(vegnet);
          if (typeof parsed === 'object' && parsed !== null) {
            // Show total across chains
            const total = Object.values(parsed).reduce((sum: number, v) => sum + (parseFloat(v as string) || 0), 0);
            return <span className="font-mono text-sm">{total.toFixed(2)}</span>;
          }
          return <span className="font-mono text-sm">{parseFloat(vegnet).toFixed(2)}</span>;
        } catch {
          return <span className="text-[#807f7e]">—</span>;
        }
      },
    },
    {
      accessorKey: 'primaryWallet',
      header: 'Wallet',
      cell: ({ row }) => {
        // Use primary wallet (highest veGNET) with fallback to last wallet
        const primaryWallet = row.getValue('primaryWallet') as string | null;
        const wallets = row.original.walletAddresses;
        const wallet = primaryWallet ?? (wallets && wallets.length > 0 ? wallets[wallets.length - 1] : null);

        if (!wallet) {
          return <span className="text-[#807f7e] text-sm">Not linked</span>;
        }

        return (
          <a
            href={`${explorerUrl}/address/${wallet}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-mono text-[#f26f56] hover:underline"
          >
            {wallet.slice(0, 6)}...{wallet.slice(-4)}
          </a>
        );
      },
    },
  ];

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue).toLowerCase();
      const entryId = String(row.getValue('entryId') || '').toLowerCase();
      const wallets = row.getValue('walletAddresses') as string[] | null;
      const walletMatch = wallets?.some(w => w.toLowerCase().includes(search)) ?? false;
      return entryId.includes(search) || walletMatch;
    },
    state: {
      sorting,
      globalFilter,
    },
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-red-500">{error}</p>
        <Button onClick={() => fetchUsers()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Refresh */}
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#807f7e]" />
          <Input
            placeholder="Search by address..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchUsers(page)}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-white">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No users found. Check connection to UB database.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#807f7e]">
          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()} users
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#807f7e] min-w-[100px] text-center">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-[#807f7e]">
          Data synced from Claimr webhooks
        </p>
      </div>
    </div>
  );
}
